import { useEffect, useState, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { EntityRelationshipGraph, EntityRelationship } from '@/components/graphs/EntityRelationshipGraph';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { apiService } from '@/lib/api-functions/api-service';

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
export function GraphNavigation() {
  const { t } = usePlatformTranslation();
    const { itemId, documentId } = useParams<{ itemId?: string; documentId?: string }>();
    const { selectedGame } = useSimplifiedUserData();
    const [relationships, setRelationships] = useState<EntityRelationship[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [containerWidth, setContainerWidth] = useState(288);
    const containerRef = useRef<HTMLDivElement>(null);

    // Determine the current item ID based on the route
    const currentItemId = useMemo(() => {
        if (documentId) return documentId; // Document detail page
        if (itemId) return itemId; // Library/glossary item page
        return null;
    }, [documentId, itemId]);

    // Measure container width for responsive graph
    useEffect(() => {
        if (!containerRef.current) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const width = entry.contentRect.width;
                if (width > 0) {
                    // Use actual pixel dimensions - browser zoom will handle visual scaling
                    setContainerWidth(width);
                }
            }
        });

        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    // Fetch relationships when game or item changes
    useEffect(() => {
        const fetchRelationships = async () => {
            if (!selectedGame?.id) {
                setRelationships([]);
                return;
            }

            setIsLoading(true);
            try {
                const result = await apiService.getAllGameRelationships(selectedGame.id);
                
                if (result.success && result.data) {
                    setRelationships(result.data);
                } else {
                    console.error('Failed to fetch relationships:', result.error);
                    setRelationships([]);
                }
            } catch (error) {
                console.error('Error fetching relationships:', error);
                setRelationships([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchRelationships();
    }, [selectedGame?.id]);

    // Don't show the graph if no relationships
    if (!selectedGame || relationships.length === 0) {
        return (
            <div ref={containerRef} className="w-full border-t border-border bg-background p-4">
                <h4 className="text-sm font-semibold mb-2 text-foreground">Graph Navigation</h4>
                <p className="text-xs text-muted-foreground">
                    {isLoading ? 'Loading relationships...' : 'No relationships found for this game.'}
                </p>
            </div>
        );
    }

    return (
        <div ref={containerRef} className="w-full border-t border-border bg-background overflow-hidden flex flex-col">
            <div className="p-3 border-b border-border bg-background">
                <h4 className="text-sm font-semibold text-foreground">{t('platform.layout.structure-menu.graph_navigation_2')}</h4>
                <p className="text-xs text-muted-foreground mt-1">
                    {currentItemId 
                        ? 'Showing connections for current item' 
                        : 'Navigate through related objects'}
                </p>
            </div>
            
            <div className="flex-1 overflow-hidden">
                <EntityRelationshipGraph
                    relationships={relationships}
                    width={containerWidth}
                    height={288}
                    initialSelectedNodeId={currentItemId || undefined}
                    showControls={false}
                    showLegend={false}
                    zoomPadding={20}
                />
            </div>
        </div>
    );
}